import "bootstrap/dist/css/bootstrap.min.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./App.css";
import React, { useEffect } from "react";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useDispatch, useSelector } from "react-redux";
import {BrowserRouter as Router} from "react-router-dom";
import AppRoutes from "./routes";
import Loader from "./components/Loader/Loader";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    getLoginConfig,
    resetLoginConfigState,
} from "./redux/slices/loginconfigSlice";
import { resetSearchFiltersState } from "./redux/slices/searchFilterSlice";
import ScrollLock from "./components/ScrollLock";
import TranslationProvider from "./i18n/TranslationProvider";
import AppConfig from "./helpers/config";
import { resetAppTranslations } from "./redux/slices/appTranslationSlice";
import { resetAppConfigState } from "./redux/slices/appconfigSlice";
import { resetBookingSliceState } from "./redux/slices/bookingSlice";
import { resetChatsState } from "./redux/slices/chatBotSlice";
import { initOneSignal } from "./onesignal";
import OneSignal from "react-onesignal";
import { useUtmTracking } from "./helpers/useUtmTracking";
import PageViewTracker from "./components/PageViewTracker/PageViewTracker";

function App() {
    const dispatch = useDispatch();
    const appconfig = useSelector((state) => state.appconfig);
    const loginConfig = useSelector(getLoginConfig);
    const savedVersion = localStorage.getItem('gt_app_version');

    useUtmTracking();

    // Check session timeout on app load and when tab becomes active
    useEffect(() => {
        const ONE_HOUR_MS = 3600000; // 1 hour in milliseconds

        const checkSessionTimeout = () => {
            const lastSessionTimestamp = localStorage.getItem('last_session_timestamp');

            if (lastSessionTimestamp) {
                const lastSessionTime = parseInt(lastSessionTimestamp);
                const currentTime = Date.now();
                const timeDifference = currentTime - lastSessionTime;

                // If more than 1 hour has passed, navigate to home and reload
                if (timeDifference > ONE_HOUR_MS) {
                    console.log('Session expired. Navigating to home and reloading...');
                    localStorage.removeItem('last_session_timestamp');
                    window.location.href = '/';
                    return;
                } else {
                    console.log('Session is still active. Updating last session timestamp.');
                }
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkSessionTimeout();
            }
        };

        checkSessionTimeout();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const setupOneSignal = async () => {
            const initialised = await initOneSignal();

            // Nothing to listen to when the SDK never came up (unsupported browser).
            if (!initialised) return;

            // 🔔 Notification shown
            OneSignal.Notifications.addEventListener(
                "foregroundWillDisplay",
                (event) => {
                    console.log("Notification displayed:", event);
                }
            );

            // 👉 Notification clicked
            OneSignal.Notifications.addEventListener(
                "click",
                (event) => {
                    console.log("Notification clicked:", event);
                }
            );
        };

        setupOneSignal().catch((error) => {
            console.warn("OneSignal: setup failed —", error?.message || error);
        });

        return () => {
            OneSignal.Notifications.removeEventListener("foregroundWillDisplay");
            OneSignal.Notifications.removeEventListener("click");
        };
    }, []);

    useEffect(() => {
        console.log(savedVersion, AppConfig.webVersion);
        if (savedVersion !== AppConfig.webVersion) {
            resetAppData();
        }
    }, [dispatch]);

    useEffect(() => {
        document.body.id = loginConfig.darkMode ? "darkMode" : "";
    }, [loginConfig.darkMode]);

    async function resetAppData() {
        console.log('Resetting app data');

        // Sequentially dispatch state resets
        dispatch(resetLoginConfigState());
        dispatch(resetAppTranslations());
        dispatch(resetAppConfigState());
        dispatch(resetBookingSliceState());
        dispatch(resetSearchFiltersState());
        dispatch(resetChatsState());
        // Clear localStorage
        // localStorage.clear();

        // Clear all cookies
        // document.cookie.split(";").forEach((c) => {
        //     document.cookie = c
        //         .replace(/^ +/, "")
        //         .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
        // });

        // Update local storage
        await new Promise((resolve) => {
            localStorage.setItem('gt_app_version', AppConfig.webVersion);
            resolve(); // Ensures the next operation waits for this
        });

        // Reload the application
        setTimeout(() => {
            window.location.reload();
        }, 500);
    }

    return (
        <GoogleOAuthProvider clientId="752153235622-du75254s7dkvo8l472g3ib552umv4pmt.apps.googleusercontent.com">
            <TranslationProvider>
                {/*<TransactionWrapper>*/}
                <React.Fragment>
                    <Router>
                        <PageViewTracker />
                        <AppRoutes />
                        {appconfig?.loadingScreen &&
                            <ScrollLock isOpen={appconfig?.loadingScreen}>
                                <Loader />
                            </ScrollLock>
                        }
                    </Router>
                    <ToastContainer
                        position="bottom-right"
                        autoClose={4000}
                        hideProgressBar={false}
                        newestOnTop={false}
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                    />
                </React.Fragment>
                {/*</TransactionWrapper>*/}
            </TranslationProvider>
        </GoogleOAuthProvider>
    );
}

export default App;
